import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface WebhookConfig {
  id: string;
  webhook_name: string;
  display_name: string;
  description: string | null;
  is_active: boolean;
  default_channel: string | null;
  default_source: string | null;
  default_stage: string | null;
  default_business_unit: string | null;
  default_deals_source: string | null;
  default_deals_channel: string | null;
  default_acquisition_channel: string | null;
  field_mapping: Record<string, string> | null;
  auth_type: string | null;
  auth_header_name: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

export function useWebhookConfigs() {
  return useQuery({
    queryKey: ['webhook-configs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webhook_configs')
        .select('*')
        .order('display_name');
      
      if (error) throw error;
      return data as WebhookConfig[];
    },
  });
}

export function useToggleWebhook() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('webhook_configs')
        .update({ is_active })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-configs'] });
    },
    onError: (error) => {
      toast.error('Erro ao atualizar webhook: ' + error.message);
    },
  });
}

export function useUpdateWebhookConfig() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<WebhookConfig> & { id: string }) => {
      const { error } = await supabase
        .from('webhook_configs')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-configs'] });
      toast.success('Configuração salva com sucesso');
    },
    onError: (error) => {
      toast.error('Erro ao salvar: ' + error.message);
    },
  });
}
